import { useEffect } from "react";
import {
  Outlet,
  createFileRoute,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { auth } from "../lib/authManager";
import { farewellPending } from "../lib/farewell";
import {
  isBareApproval,
  isHostedOnlyPath,
  isLocalMode,
  nextFromLocation,
  stripBase,
} from "../lib/appMode";
import { useAuth } from "../lib/useAuth";
import { useActivityHubBridge } from "../lib/activityHub";
import { useSearchHotkey } from "../lib/searchOverlay";
import { useSources } from "../sources";
import { AppBar } from "../components/app-bar";
import { PageBackdrop } from "../components/page-backdrop";
import { SearchOverlay } from "../components/search-overlay";

export const Route = createFileRoute("/_authed")({
  beforeLoad: ({ location }) => {
    if (auth.getState().status !== "signedIn") {
      // pathname + query: a /link?code=… deep link must survive the sign-in
      // round trip (the sign-in page navigates by href when `next` has one).
      throw redirect({
        to: "/auth/signin",
        search: { next: nextFromLocation(location) },
      });
    }
    // The server-hosted app is this server only: the cloud surfaces
    // (Discover, Watchlist, profile, claim/link, account settings) live on
    // app.rago.tv.
    if (isLocalMode && isHostedOnlyPath(location.pathname)) {
      throw redirect({ to: "/" });
    }
  },
  component: AuthedLayout,
});

function AuthedLayout() {
  // The broker's verdict can land after the page painted (bootstrap paints
  // from the stored profile first; any 401 re-checks; a sign-out in another
  // tab arrives over the channel): the moment the session is gone, go to
  // sign-in with the way back. The approval pages too (/link, and the
  // setup wizard's /claim/approve): a sign-out from their account chip lands
  // on sign-in, and `next` (the code and its popup=1 / redirect=1 included)
  // brings it back to the same request.
  const state = useAuth();
  const navigate = useNavigate();
  const bare = useRouterState({ select: (s) => isBareApproval(s.location) });
  useEffect(() => {
    if (state.status !== "signedOut") return;
    // The account was just deleted from this tab (Settings › Account): the
    // session can be found gone before the dialog has reached the goodbye
    // page — a request in flight answers 401 — and that page is where this
    // tab goes, not sign-in.
    if (farewellPending()) {
      void navigate({ to: "/auth/deleted", replace: true });
      return;
    }
    void navigate({
      to: "/auth/signin",
      search: {
        next: nextFromLocation({
          pathname: stripBase(window.location.pathname),
          searchStr: window.location.search || undefined,
        }),
      },
    });
  }, [state.status, navigate]);

  // Everything below is the account's. With the session gone nothing of it
  // is drawn while the effect above leaves — lib/accountScope has just
  // emptied the query cache and the registry, and a page still mounted
  // would ask again with no session. Keyed by the account, so one that
  // changes with no sign-out in this tab (a sign-in in another tab) starts
  // from a new tree instead of the last account's page state.
  if (state.status !== "signedIn") return null;
  const account = state.user.id;

  // An approval page, in its popup or its own tab: the wordmark and
  // account chip are the page's, no bar, no search, no server sockets.
  if (bare) {
    return (
      <>
        <PageBackdrop />
        <main key={account} className="min-h-screen">
          <Outlet />
        </main>
      </>
    );
  }
  return <AppShell key={account} />;
}

function AppShell() {
  // Hydrate the source registry app-wide so getSource()/getSources() work on
  // every page — including direct loads of /player and /item — and so the
  // bar's chip re-renders as servers come online.
  useSources();

  // One events-socket lease per owned online server, app-wide: feeds the
  // bar's activity menu and centralizes scan-finish query invalidation.
  useActivityHubBridge();

  // Cmd/Ctrl+K → the search overlay, everywhere but the player.
  useSearchHotkey();

  // The window is the one scroll container; the bar floats fixed over it.
  // Hero pages run edge-to-edge underneath, everything else clears it with
  // .rago-page. The default backdrop is mounted here, ONCE, outside <main>:
  // every page shares one decoded still (no re-fetch, no re-fade between
  // Profile, Settings and Library), and pages with their own art hide it
  // through lib/pageBackdrop.
  return (
    <>
      <PageBackdrop />
      <AppBar />
      <main className="min-h-screen">
        <Outlet />
      </main>
      <SearchOverlay />
    </>
  );
}
